import React, { useState } from 'react';
import { ChevronLeft, Mail, Lock, User, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
interface AuthScreenProps {
  initialMode: 'login' | 'signup';
  onBack: () => void;
  onSuccess: () => void;
  onForgotPassword?: () => void;
}
export function AuthScreen({
  initialMode,
  onBack,
  onSuccess,
  onForgotPassword
}: AuthScreenProps) {
  const [mode, setMode] = useState<'login' | 'signup'>(initialMode);
  const [isLoading, setIsLoading] = useState(false);
  // Form states
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  // Simple validation state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    // Basic mock validation
    const newErrors: Record<string, string> = {};
    if (!email) newErrors.email = 'Email is required';else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Email is invalid';
    if (!password) newErrors.password = 'Password is required';else if (password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    if (mode === 'signup') {
      if (!name) newErrors.name = 'Full name is required';
      if (password !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setIsLoading(true);
    // Mock API call
    setTimeout(() => {
      setIsLoading(false);
      onSuccess();
    }, 1500);
  };
  const toggleMode = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    setErrors({});
  };
  return <div className="flex flex-col h-full w-full bg-stone-50 overflow-y-auto" data-id="element-414">
      {/* Header */}
      <div className="flex items-center justify-between p-6 pt-8 sticky top-0 bg-stone-50/90 backdrop-blur-md z-20" data-id="element-415">
        <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-slate-200/50 text-slate-600 transition-colors" aria-label="Go back" data-id="element-416">
          <ChevronLeft size={28} data-id="element-417" />
        </button>
        {/* Small Mascot Thumbnail */}
        <div className="w-12 h-12 rounded-2xl overflow-hidden border-2 border-white shadow-sm bg-stone-200" data-id="element-418">
          <video autoPlay loop muted playsInline className="w-full h-full object-cover" src="https://cdn.magicpatterns.com/uploads/69mUG6ooMsKbP89Ta37mkt/Katethought_friendly_gym_mascot_character_funny_good_guy_musc_bc3cfd10-111a-4766-8e76-81af2852edc8_2.mp4" data-id="element-419" />
        </div>
        <div className="w-10" data-id="element-420" /> {/* Spacer for centering */}
      </div>

      <div className="px-6 pb-8 flex-1 flex flex-col" data-id="element-421">
        {/* Title Area */}
        <div className="mb-8 mt-2" data-id="element-422">
          <h1 className="text-3xl font-extrabold text-slate-800 mb-2" data-id="element-423">
            {mode === 'login' ? 'Welcome back!' : 'Create Account'}
          </h1>
          <p className="text-slate-500 font-medium" data-id="element-424">
            {mode === 'login' ? 'Ready to crush your fitness goals today?' : 'Join GymBuddy and start your fitness journey.'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5 flex-1" data-id="element-425">
          {mode === 'signup' && <Input label="Full Name" icon={User} placeholder="Flex Muscleman" value={name} onChange={e => setName(e.target.value)} error={errors.name} data-id="element-426" />}

          <Input label="Email Address" icon={Mail} type="email" placeholder="flex@gymbuddy.app" value={email} onChange={e => setEmail(e.target.value)} error={errors.email} data-id="element-427" />

          <Input label="Password" icon={Lock} type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} error={errors.password} data-id="element-428" />

          {mode === 'signup' && <Input label="Confirm Password" icon={Lock} type="password" placeholder="••••••••" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} error={errors.confirmPassword} data-id="element-429" />}

          {/* Login Extras */}
          {mode === 'login' && <div className="flex items-center justify-end pt-1" data-id="element-430">
              <button type="button" onClick={onForgotPassword} className="text-sm font-bold text-emerald-600 hover:text-emerald-700" data-id="element-431">
                Forgot password?
              </button>
            </div>}

          {/* Signup Extras */}
          {mode === 'signup' && <p className="text-xs text-slate-500 font-medium leading-relaxed pt-2" data-id="element-432">
              By signing up, you agree to our{' '}
              <a href="#" className="font-bold text-emerald-600 hover:underline" data-id="element-433">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="#" className="font-bold text-emerald-600 hover:underline" data-id="element-434">
                Privacy Policy
              </a>
              .
            </p>}

          <div className="pt-6 space-y-4" data-id="element-435">
            <Button type="submit" variant={mode === 'login' ? 'primary' : 'secondary'} fullWidth isLoading={isLoading} data-id="element-436">
              {mode === 'login' ? 'Login' : 'Create Account'}
            </Button>

            <div className="relative flex items-center py-2" data-id="element-437">
              <div className="flex-grow border-t border-slate-200" data-id="element-438"></div>
              <span className="flex-shrink-0 mx-4 text-slate-400 text-sm font-bold uppercase tracking-wider" data-id="element-439">
                Or
              </span>
              <div className="flex-grow border-t border-slate-200" data-id="element-440"></div>
            </div>

            <Button type="button" variant="google" fullWidth data-id="element-441">
              Continue with Google
            </Button>
          </div>
        </form>

        {/* Toggle Mode */}
        <div className="mt-8 text-center pb-6" data-id="element-442">
          <p className="text-slate-600 font-medium" data-id="element-443">
            {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}{' '}
            <button onClick={toggleMode} className={`font-bold hover:underline ${mode === 'login' ? 'text-pink-500' : 'text-emerald-500'}`} data-id="element-444">
              {mode === 'login' ? 'Sign up' : 'Log in'}
            </button>
          </p>
        </div>
      </div>
    </div>;
}